import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, BookOpen, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

/**
 * Shows the word lists available for the user's school + grade and lets
 * them set the active one. Renders inline (no modal).
 *
 * Props:
 *   compact?: boolean    // tighter padding for Dashboard
 *   onChanged?: (activeId) => void  // notify parent so it can refresh
 */
export default function ActiveWordListPicker({ compact = false, onChanged }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [packs, setPacks] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [reason, setReason] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/me/active-pack");
      // Packs come back with the active one first; we re-sort alphabetically
      // for the picker UI and highlight the active one with a checkmark.
      const sorted = [...(data.packs || [])].sort((a, b) =>
        (a.name || "").toLowerCase().localeCompare((b.name || "").toLowerCase())
      );
      setPacks(sorted);
      setActiveId(data.active_id || null);
      setReason(data.reason || null);
    } catch (err) {
      console.warn("[ActiveWordListPicker] load failed", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const choose = async (pid) => {
    if (pid === activeId || saving) return;
    setSaving(true);
    try {
      await api.post("/me/active-wordlist", { wordlist_id: pid });
      setActiveId(pid);
      toast.success("Active word list updated");
      onChanged && onChanged(pid);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not switch word list");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="card-soft text-slate-500 flex items-center gap-2 font-semibold" data-testid="active-wordlist-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading word lists…
      </div>
    );
  }

  if (!packs.length) {
    return (
      <div className="card-soft text-center py-6" data-testid="active-wordlist-empty">
        <Sparkles className="w-8 h-8 mx-auto text-amber-400" strokeWidth={3} />
        <div className="font-display text-lg font-bold text-[#1E1B4B] mt-2">No word lists yet</div>
        <p className="text-sm text-slate-600 font-semibold mt-1">
          {reason === "no_grade"
            ? "Pick your grade on your profile to see word lists for your class."
            : "Ask your teacher or admin to assign a word pack for your school + grade."}
        </p>
        {reason === "no_grade" && (
          <Link to="/profile" className="btn-clay-blue mt-4 inline-flex" data-testid="active-wordlist-go-profile">
            Go to profile
          </Link>
        )}
      </div>
    );
  }

  return (
    <div className={compact ? "card-soft p-4" : "card-soft"} data-testid="active-wordlist-picker">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-[#4D96FF]" strokeWidth={3} />
          <h3 className="font-display text-lg font-bold text-[#1E1B4B]">Available word lists</h3>
        </div>
        <span className="chip" data-testid="active-wordlist-count">{packs.length}</span>
      </div>
      <ul className="space-y-2" data-testid="active-wordlist-list">
        {packs.map((p) => {
          const isActive = p.id === activeId;
          const schoolBadge = p.school_id ? "School pack" : "Default";
          return (
            <motion.li
              key={p.id}
              initial={{ x: -8, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.2 }}
            >
              <button
                type="button"
                disabled={saving}
                onClick={() => choose(p.id)}
                className={`w-full flex items-center justify-between gap-3 rounded-2xl px-4 py-3 border-4 text-left transition-colors ${
                  isActive
                    ? "bg-[#ECFCCB] border-[#22C55E] shadow-[0_3px_0_#15803D]"
                    : "bg-white border-blue-100 hover:border-[#4D96FF] hover:bg-blue-50"
                }`}
                data-testid={`active-wordlist-row-${p.id}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div
                    className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${
                      isActive ? "bg-[#22C55E] text-white" : "bg-slate-100 text-slate-400"
                    }`}
                    data-testid={`active-wordlist-check-${p.id}`}
                  >
                    {isActive ? <Check className="w-4 h-4" strokeWidth={4} /> : null}
                  </div>
                  <div className="min-w-0">
                    <div className="font-display text-base font-bold text-[#1E1B4B] truncate">
                      {p.name}
                    </div>
                    <div className="text-xs text-slate-500 font-semibold flex items-center gap-2">
                      <span>{p.word_count || (p.words || []).length} words</span>
                      <span>·</span>
                      <span className={p.school_id ? "text-[#4D96FF]" : ""}>{schoolBadge}</span>
                    </div>
                  </div>
                </div>
                {isActive && (
                  <span
                    className="shrink-0 text-xs font-bold uppercase tracking-wider text-[#15803D]"
                    data-testid={`active-wordlist-badge-${p.id}`}
                  >
                    Active
                  </span>
                )}
              </button>
            </motion.li>
          );
        })}
      </ul>
      <p className="text-xs text-slate-500 font-semibold mt-3">
        Tap any list to make it your active list. Practice, Test and Listen will use it.
      </p>
    </div>
  );
}
